import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Lock, Pencil, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ConfirmDialog, tdClass, thClass } from "../components/Common";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Roles() {
  const { hasPerm } = useAuth();
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, target: null });
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const canSeeSpecial = hasPerm("view_special_expense");
  const visibleGroups = canSeeSpecial
    ? groups
    : groups.filter((g) => g.group !== "Pengeluaran Khusus");

  const load = useCallback(() => {
    Promise.all([api.get("/roles"), api.get("/permissions")])
      .then(([r, p]) => { setRoles(r.data); setGroups(p.data); })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const openAdd = () => {
    setLabel(""); setDescription(""); setSelected(["view_dashboard"]);
    setModal({ open: true, target: null });
  };

  const openEdit = (role) => {
    setLabel(role.label);
    setDescription(role.description || "");
    setSelected(
      role.permissions.includes("*")
        ? (canSeeSpecial ? groups : groups.filter((g) => g.group !== "Pengeluaran Khusus")).flatMap((g) => g.items.map((i) => i.key))
        : role.permissions
    );
    setModal({ open: true, target: role });
  };

  const toggle = (key) =>
    setSelected((s) => (s.includes(key) ? s.filter((x) => x !== key) : [...s, key]));

  const save = async () => {
    if (!label.trim()) {
      toast.error("Nama role wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const payload = { label, description, permissions: selected };
      if (modal.target) {
        await api.put(`/roles/${modal.target.id}`, payload);
        toast.success("Role diperbarui — berlaku langsung untuk seluruh penggunanya");
      } else {
        await api.post("/roles", payload);
        toast.success("Role baru berhasil dibuat");
      }
      setModal({ open: false, target: null });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setDeleteLoading(true);
    try {
      await api.delete(`/roles/${deleting.id}`);
      toast.success("Role dihapus");
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setDeleteLoading(false);
    }
  };

  const isSuper = modal.target?.slug === "super_admin";

  if (loading)
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="roles-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-100">Role & Permission</h2>
          <p className="text-xs text-slate-400">Atur hak akses setiap role tanpa mengubah kode</p>
        </div>
        {hasPerm("manage_roles") && (
          <button
            data-testid="btn-add-role"
            onClick={openAdd}
            className="bg-slate-900 text-white hover:bg-slate-800 font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm shadow-sm"
          >
            <Plus className="w-4 h-4" /> Buat Role Baru
          </button>
        )}
      </div>

      <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table data-testid="table-roles" className="w-full border-collapse text-sm text-left">
            <thead>
              <tr>
                <th className={thClass}>Role</th>
                <th className={thClass}>Deskripsi</th>
                <th className={thClass}>Permission</th>
                <th className={thClass}>Jenis</th>
                <th className={`${thClass} text-center`}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.id} className="hover:bg-slate-800/40" data-testid={`row-role-${r.slug || r.id}`}>
                  <td className={tdClass}>
                    <span className="font-semibold text-slate-100 flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-slate-400" /> {r.label}
                    </span>
                  </td>
                  <td className={`${tdClass} text-slate-400 max-w-[280px]`}><span className="line-clamp-2">{r.description || "-"}</span></td>
                  <td className={tdClass}>
                    <span className="bg-slate-800 text-slate-200 rounded-full px-2.5 py-0.5 text-xs font-medium">
                      {r.permissions.includes("*") ? "Semua (penuh)" : `${r.permissions.length} permission`}
                    </span>
                  </td>
                  <td className={tdClass}>
                    {r.builtin ? (
                      <span className="bg-blue-500/15 text-blue-300 border border-blue-200 rounded-full px-2.5 py-0.5 text-xs font-semibold">Bawaan</span>
                    ) : (
                      <span className="bg-amber-500/10 text-amber-300 border border-amber-500/30 rounded-full px-2.5 py-0.5 text-xs font-semibold">Custom</span>
                    )}
                  </td>
                  <td className={`${tdClass} text-center`}>
                    <div className="flex items-center justify-center gap-1">
                      {r.slug === "super_admin" ? (
                        <span className="text-xs text-slate-400 flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> Terkunci</span>
                      ) : (
                        <>
                          <button data-testid={`btn-edit-role-${r.id}`} title="Edit Permission" onClick={() => openEdit(r)} className="p-1.5 rounded-md hover:bg-slate-800/40 text-slate-400 hover:text-blue-300"><Pencil className="w-4 h-4" /></button>
                          {!r.builtin && (
                            <button data-testid={`btn-delete-role-${r.id}`} title="Hapus" onClick={() => setDeleting(r)} className="p-1.5 rounded-md hover:bg-rose-500/15 text-slate-400 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={modal.open} onOpenChange={(o) => setModal((m) => ({ ...m, open: o }))}>
        <DialogContent data-testid="modal-role-form" className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{modal.target ? `Edit Role — ${modal.target.label}` : "Buat Role Baru"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Nama Role *</Label>
                <Input data-testid="input-role-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Contoh: Bendahara" disabled={isSuper} />
              </div>
              <div className="space-y-1.5">
                <Label>Deskripsi</Label>
                <Input data-testid="input-role-description" value={description} onChange={(e) => setDescription(e.target.value)} disabled={isSuper} />
              </div>
            </div>
            {isSuper ? (
              <p className="text-sm text-slate-400 rounded-lg bg-[#0F172A] border border-slate-800 p-3">
                Super Admin memiliki seluruh permission secara permanen dan tidak dapat diubah.
              </p>
            ) : (
              <div className="space-y-3">
                {visibleGroups.map((g) => (
                  <div key={g.group} className="rounded-lg border border-slate-800 p-3">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">{g.group}</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                      {g.items.map((item) => (
                        <label key={item.key} className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer hover:bg-slate-800/40 rounded px-1.5 py-1">
                          <input
                            type="checkbox"
                            data-testid={`perm-${item.key}`}
                            className="accent-slate-900 w-4 h-4"
                            checked={selected.includes(item.key)}
                            onChange={() => toggle(item.key)}
                          />
                          {item.label}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal((m) => ({ ...m, open: false }))}>Batal</Button>
            {!isSuper && (
              <Button data-testid="btn-role-save" className="bg-slate-900 hover:bg-slate-800 text-white" onClick={save} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Simpan Role
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Hapus Role?"
        description={deleting ? `Role "${deleting.label}" akan dihapus permanen.` : ""}
        onConfirm={doDelete}
        loading={deleteLoading}
      />
    </div>
  );
}
